// One place for every KPI's label, unit and formula, so the cards, the chart and
// the definitions table can't drift apart. Values come from GET /api/kpi.

export const METRICS = {
    acquisitionSource: {
        label: 'Acquisition Source',
        notTracked: 'Signup does not record a source or channel yet.'
    },
    activeUsers: {
        label: 'Active Users', unit: 'count', color: 'primary',
        get: (m) => m.activeUsers,
        definition: 'Distinct accounts that rated, commented, spotlighted, uploaded, played, liked or viewed a song, or tapped a boost link, in the period. Website page views are not included.'
    },
    returningUsers: {
        label: 'Returning Users', unit: 'count', color: 'primary',
        get: (m) => m.returningUsers,
        definition: 'Active users whose account was created before the period started. Accounts with no recorded signup date count as existing; deleted accounts are excluded.'
    },
    spotlightUsage: {
        label: 'Spotlight Usage', unit: 'count', color: 'info',
        get: (m) => m.spotlightUsage,
        definition: 'Songs spotlighted (new spotlight records). Re-adding a removed spotlight is not counted again.'
    },
    boostUsage: {
        label: 'Boost Usage', unit: 'count', color: 'info',
        get: (m) => m.boostUsage,
        definition: "Taps on an artist's activation links from the Social Share page. Taps from the feed's boost sheet are not recorded yet, so this undercounts."
    },
    commentUsage: {
        label: 'Comment Usage', unit: 'count', color: 'info',
        get: (m) => m.commentUsage,
        definition: 'Comments on songs plus comments on spotlights.'
    },
    avgListeningSec: {
        label: 'Avg Listening Time / User', unit: 'sec', color: 'info',
        get: (m) => m.avgListeningSec,
        definition: 'Stand-in for engagement time: total listening time ÷ users who listened. A play is recorded when a song stops or ends, not when the app goes to the background.'
    },
    sessionTime: {
        label: 'Avg Session Time / User',
        notTracked: 'The app does not record sessions or time in the app yet.'
    },
    avgRatingsPerRater: {
        label: 'Avg Ratings / User', unit: 'ratio', color: 'info',
        get: (m) => m.avgRatingsPerRater,
        definition: 'Ratings submitted ÷ distinct users who rated in the period.'
    },
    retentionD1: {
        label: 'Retention D1', unit: '%', color: 'success',
        get: (m) => m.retention.d1.rate,
        detail: (m) => `${m.retention.d1.retained} of ${m.retention.d1.eligible} new users`,
        definition: 'Of accounts created in the period whose day 1 has fully passed, the % active on the day after signup (UTC days).'
    },
    retentionD7: {
        label: 'Retention D7', unit: '%', color: 'success',
        get: (m) => m.retention.d7.rate,
        detail: (m) => `${m.retention.d7.retained} of ${m.retention.d7.eligible} new users`,
        definition: 'Of accounts created in the period whose day 7 has fully passed, the % active exactly 7 days after signup.'
    },
    retentionD30: {
        label: 'Retention D30', unit: '%', color: 'success',
        get: (m) => m.retention.d30.rate,
        detail: (m) => `${m.retention.d30.retained} of ${m.retention.d30.eligible} new users`,
        definition: 'Of accounts created in the period whose day 30 has fully passed, the % active exactly 30 days after signup.'
    },
    churnRate: {
        label: 'Churn Rate', unit: '%', color: 'error', better: 'down',
        get: (m) => m.churnRate,
        detail: (m) => `${m.churned} users stopped`,
        definition: 'Of the users active in the previous period, the % not active in this period. Lower is better.'
    },
    ttfaAvgHours: {
        label: 'Time to First Action', unit: 'hours', color: 'warning', better: 'down',
        get: (m) => m.timeToFirstAction.avgHours,
        detail: (m) => (m.timeToFirstAction.medianHours === null ? '' : `median ${m.timeToFirstAction.medianHours} h`),
        definition: 'Average hours from signup to the first rating, upload, comment or spotlight, for accounts created in the period that have acted so far.'
    },
    activationRate: {
        label: 'Activation Rate', unit: '%', color: 'warning',
        get: (m) => m.timeToFirstAction.activationRate,
        detail: (m) => `${m.timeToFirstAction.activated} of ${m.newAccounts} new users`,
        definition: 'Of accounts created in the period, the % that have made a first rating, upload, comment or spotlight so far.'
    },
    ratingsPerUpload: {
        label: 'Upload-to-Rating Ratio', unit: 'ratio', color: 'primary',
        get: (m) => m.ratingsPerUpload,
        detail: (m) => `${m.ratings} ratings / ${m.uploads} uploads`,
        definition: 'Ratings submitted in the period ÷ songs uploaded in the period (ratings per upload).'
    },
    funnel: {
        label: 'Upload / Rating Funnel',
        notTracked: 'The individual upload and rating steps (file picked, upload failed, rating opened or abandoned) are not recorded yet.'
    },
    arpuUsd: {
        label: 'ARPU', unit: 'usd', color: 'success',
        get: (m) => m.arpuUsd,
        definition: 'Revenue ÷ active users in the period.'
    },
    revenueUsd: {
        label: 'Revenue', unit: 'usd', color: 'success',
        get: (m) => m.revenueUsd,
        definition: "Token purchases (credited, not refunded) at USD list price, before Apple's or Google's cut. In-app purchases started on 20 Aug 2026."
    },
    payingUsers: {
        label: 'Paying Users', unit: 'count', color: 'success',
        get: (m) => m.payingUsers,
        definition: 'Distinct users with a credited token purchase in the period.'
    }
};

// Grouped as in the KPI request. New users, ratings and uploads live on the Dashboard.
// Four headline numbers, then short grouped lists; not-tracked metrics get one line.
export const HEADLINE = ['activeUsers', 'returningUsers', 'churnRate', 'revenueUsd'];

export const SECTIONS = [
    { title: 'Engagement', keys: ['spotlightUsage', 'boostUsage', 'commentUsage', 'avgRatingsPerRater', 'avgListeningSec'] },
    { title: 'Retention & Activation', keys: ['retentionD1', 'retentionD7', 'retentionD30', 'activationRate', 'ttfaAvgHours'] },
    { title: 'Content & Monetization', keys: ['ratingsPerUpload', 'arpuUsd', 'payingUsers'] }
];

export const NOT_TRACKED = ['acquisitionSource', 'sessionTime', 'funnel'];

export function formatValue(unit, v) {
    if (v === null || v === undefined) return '—';
    switch (unit) {
        case '%': return `${v}%`;
        case 'sec': return v >= 60 ? `${Math.floor(v / 60)}m ${Math.round(v % 60)}s` : `${Math.round(v)}s`;
        case 'hours': return v >= 48 ? `${Math.round((v / 24) * 10) / 10} d` : `${v} h`;
        case 'ratio': return Number(v).toFixed(2);
        case 'usd': return `$${Number(v).toFixed(2)}`;
        default: return Number(v).toLocaleString();
    }
}

// Rates change in points; everything else in percent of the previous value.
export function changeOf(def, current, previous) {
    const v = current === undefined ? null : current;
    const p = previous === undefined ? null : previous;
    if (v === null || p === null) return null;
    if (def.unit === '%') return { kind: 'pts', value: Math.round((v - p) * 10) / 10 };
    if (p === 0) return v === 0 ? { kind: 'pct', value: 0 } : null;
    return { kind: 'pct', value: Math.round(((v - p) / p) * 1000) / 10 };
}
